import { tagsService } from '@/services/tagsService';
import {
  Calendar,
  Check,
  Download,
  Filter,
  Loader2,
  Search,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Export Contacts Modal
 * Provides options to export all contacts or filtered contacts by tags and date range.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onExport - Function to trigger export with filters
 * @param {string} props.projectId - Current project ID
 */
const ExportContactsModal = ({ isOpen, onClose, onExport, projectId }) => {
  const [exportType, setExportType] = useState('all'); // 'all' or 'filtered'
  const [selectedTags, setSelectedTags] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Tag implementation states
  const [tags, setTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const observerTarget = useRef(null);

  const fetchTags = useCallback(
    async (pageNum, query = '', reset = false) => {
      if (!projectId) return;

      try {
        if (pageNum === 1 && !reset) setIsSearching(true);
        else setIsLoadingTags(true);

        let response;
        if (query) {
          // Search API might not support pagination in the same way,
          // but tagsService.getTags does. Let's use getTags with search param if available.
          // Based on tagsService.js: getTags: async (projectId, params = {}) => { ... }
          response = await tagsService.getTags(projectId, {
            search: query,
            page: pageNum,
            limit: 20,
          });
        } else {
          response = await tagsService.getTags(projectId, {
            page: pageNum,
            limit: 20,
          });
        }

        const newTags = response.docs || [];
        setTags(prev => (reset ? newTags : [...prev, ...newTags]));
        setHasMore(response.hasNextPage || false);
        setPage(pageNum);
      } catch (error) {
        console.error('Error loading tags:', error);
      } finally {
        setIsSearching(false);
        setIsLoadingTags(false);
      }
    },
    [projectId]
  );

  // Initial load and search debouncing
  useEffect(() => {
    if (isOpen && exportType === 'filtered' && isDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchTags(1, searchQuery, true);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, exportType, searchQuery, isDropdownOpen, fetchTags]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore || isLoadingTags || !isDropdownOpen) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          fetchTags(page + 1, searchQuery);
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoadingTags, isDropdownOpen, page, searchQuery, fetchTags]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTagToggle = tag => {
    const tagId = tag._id;
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t._id === tagId);
      if (isSelected) {
        return prev.filter(t => t._id !== tagId);
      } else {
        return [...prev, tag];
      }
    });
  };

  const handleExport = () => {
    if (exportType === 'all') {
      onExport({});
      return;
    }

    const filters = {
      projectId,
      tags: selectedTags.map(t => t._id).join(','),
      from: dateRange.start,
      to: dateRange.end,
    };

    onExport(filters);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl transition-all animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <Download className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Export Contacts
              </h3>
              <p className="text-sm text-gray-500">
                Choose how you want to export your contacts
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Export Type Selection */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setExportType('all')}
                className={`flex flex-col items-center gap-3 rounded-xl border-2 p-4 pt-5 transition-all ${
                  exportType === 'all'
                    ? 'border-primary-600 bg-primary-50/50 ring-1 ring-primary-600'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div
                  className={`rounded-lg p-2 ${exportType === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                >
                  <Users className="h-5 w-5" />
                </div>
                <div className="text-center">
                  <span
                    className={`block text-sm font-semibold ${exportType === 'all' ? 'text-primary-900' : 'text-gray-700'}`}
                  >
                    All Contacts
                  </span>
                  <span className="text-xs text-gray-500">
                    Export every contact
                  </span>
                </div>
              </button>

              <button
                onClick={() => setExportType('filtered')}
                className={`flex flex-col items-center gap-3 rounded-xl border-2 p-4 pt-5 transition-all ${
                  exportType === 'filtered'
                    ? 'border-primary-600 bg-primary-50/50 ring-1 ring-primary-600'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div
                  className={`rounded-lg p-2 ${exportType === 'filtered' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                >
                  <Filter className="h-5 w-5" />
                </div>
                <div className="text-center">
                  <span
                    className={`block text-sm font-semibold ${exportType === 'filtered' ? 'text-primary-900' : 'text-gray-700'}`}
                  >
                    Filtered
                  </span>
                  <span className="text-xs text-gray-500">
                    Select specific criteria
                  </span>
                </div>
              </button>
            </div>

            {/* Filter Options (Conditional) */}
            {exportType === 'filtered' && (
              <div className="space-y-5 rounded-2xl border border-gray-100 bg-gray-50/50 p-5 animate-in slide-in-from-top-2 duration-300">
                {/* Tags Filter - Multi-select with Search & Infinite Scroll */}
                <div className="relative" ref={dropdownRef}>
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="h-4 w-4 text-gray-500" />
                    <label className="text-sm font-semibold text-gray-700">
                      Filter by Tags
                    </label>
                  </div>

                  {/* Field Display */}
                  <div
                    onClick={() => setIsDropdownOpen(true)}
                    className="flex min-h-[42px] w-full cursor-pointer flex-wrap gap-1.5 rounded-xl border border-gray-200 bg-white p-2 transition-all focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
                  >
                    {selectedTags.length > 0 ? (
                      selectedTags.map(tag => (
                        <span
                          key={tag._id}
                          className="flex items-center gap-1 rounded-lg bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700"
                        >
                          {tag.name}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-primary-900"
                            onClick={e => {
                              e.stopPropagation();
                              handleTagToggle(tag);
                            }}
                          />
                        </span>
                      ))
                    ) : (
                      <span className="flex items-center text-sm text-gray-400 pl-1">
                        Select tags...
                      </span>
                    )}
                  </div>

                  {/* Dropdown */}
                  {isDropdownOpen && (
                    <div className="absolute left-0 right-0 z-[60] mt-2 max-h-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* Search Input */}
                      <div className="flex items-center border-b border-gray-100 px-3 py-2">
                        <Search className="h-4 w-4 text-gray-400" />
                        <input
                          autoFocus
                          type="text"
                          placeholder="Search tags..."
                          className="w-full bg-transparent px-2 text-sm outline-none"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                        />
                        {isSearching && (
                          <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
                        )}
                      </div>

                      {/* Tags List */}
                      <div className="max-h-56 overflow-y-auto p-1 custom-scrollbar">
                        {tags.length > 0 ? (
                          <>
                            {tags.map(tag => {
                              const isSelected = selectedTags.some(
                                t => t._id === tag._id
                              );
                              return (
                                <button
                                  key={tag._id}
                                  onClick={() => handleTagToggle(tag)}
                                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${isSelected ? 'bg-primary-50/50 text-primary-700' : 'text-gray-700'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-2 w-2 rounded-full"
                                      style={{
                                        backgroundColor: tag.color || '#3B82F6',
                                      }}
                                    />
                                    <span>{tag.name}</span>
                                  </div>
                                  {isSelected && (
                                    <Check className="h-4 w-4 text-primary-600" />
                                  )}
                                </button>
                              );
                            })}
                            {/* Infinite Scroll Trigger */}
                            <div
                              ref={observerTarget}
                              className="flex h-8 items-center justify-center"
                            >
                              {isLoadingTags && (
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                              )}
                            </div>
                          </>
                        ) : !isSearching ? (
                          <div className="px-4 py-8 text-center">
                            <p className="text-sm text-gray-500">
                              No tags found
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                {/* Date Range Filter */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <label className="text-sm font-semibold text-gray-700">
                      Created At
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold ml-1">
                        Start Date
                      </span>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={e =>
                          setDateRange(prev => ({
                            ...prev,
                            start: e.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold ml-1">
                        End Date
                      </span>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={e =>
                          setDateRange(prev => ({
                            ...prev,
                            end: e.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4 rounded-b-2xl">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-2 text-sm font-semibold text-white shadow-md shadow-primary-200 hover:bg-primary-700 active:scale-95 transition-all"
          >
            <Download className="h-4 w-4" />
            Export Contacts
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportContactsModal;
